import * as React from "react";

function objectIs<T>(a: T, b: T): boolean {
  return Object.is(a, b);
}

export function useSyncExternalStoreWithSelector<TSnapshot, TSelection>(
  subscribe: (onStoreChange: () => void) => () => void,
  getSnapshot: () => TSnapshot,
  getServerSnapshot: (() => TSnapshot) | undefined,
  selector: (snapshot: TSnapshot) => TSelection,
  isEqual?: (a: TSelection, b: TSelection) => boolean,
): TSelection {
  const instRef = React.useRef<{
    hasValue: boolean;
    value: TSelection | null;
  } | null>(null);

  if (instRef.current === null) {
    instRef.current = {
      hasValue: false,
      value: null,
    };
  }

  const [getSelection, getServerSelection] = React.useMemo(() => {
    let hasMemo = false;
    let memoizedSnapshot: TSnapshot;
    let memoizedSelection: TSelection;

    const memoizedSelector = (nextSnapshot: TSnapshot) => {
      if (!hasMemo) {
        hasMemo = true;
        memoizedSnapshot = nextSnapshot;

        const nextSelection = selector(nextSnapshot);
        const current = instRef.current;
        if (
          isEqual &&
          current?.hasValue &&
          current.value !== null &&
          isEqual(current.value, nextSelection)
        ) {
          memoizedSelection = current.value;
          return current.value;
        }

        memoizedSelection = nextSelection;
        return nextSelection;
      }

      if (objectIs(memoizedSnapshot, nextSnapshot)) {
        return memoizedSelection;
      }

      const nextSelection = selector(nextSnapshot);
      if (isEqual && isEqual(memoizedSelection, nextSelection)) {
        memoizedSnapshot = nextSnapshot;
        return memoizedSelection;
      }

      memoizedSnapshot = nextSnapshot;
      memoizedSelection = nextSelection;
      return nextSelection;
    };

    return [
      () => memoizedSelector(getSnapshot()),
      getServerSnapshot
        ? () => memoizedSelector(getServerSnapshot())
        : undefined,
    ] as const;
  }, [getSnapshot, getServerSnapshot, selector, isEqual]);

  const value = React.useSyncExternalStore(
    subscribe,
    getSelection,
    getServerSelection,
  );

  React.useEffect(() => {
    const current = instRef.current;
    if (current) {
      current.hasValue = true;
      current.value = value;
    }
  }, [value]);

  React.useDebugValue(value);
  return value;
}
